import { useEffect, useMemo, useState } from "react";
import client from "../api/client";

const emptyForm = {
  empCode: "",
  firstName: "",
  lastName: "",
  department: "",
  designation: "",
  joinDate: "",
  basicSalary: 0,
  hra: 0,
  allowance: 0,
};

export default function EmployeesPage() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => {
    try {
      const res = await client.get("/hr/employees");
      setRows(res.data || []);
    } catch (error) {
      console.error(error);
      alert("Failed to load employees");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!form.empCode.trim() || !form.firstName.trim() || !form.department.trim() || !form.designation.trim() || !form.joinDate) {
      alert("Employee code, first name, department, designation and join date are required");
      return;
    }

    try {
      setSaving(true);
      await client.post("/hr/employees", {
        ...form,
        companyId: Number(localStorage.getItem("companyId") || 0),
        basicSalary: Number(form.basicSalary || 0),
        hra: Number(form.hra || 0),
        allowance: Number(form.allowance || 0),
      });
      setForm(emptyForm);
      await load();
    } finally {
      setSaving(false);
    }
  };

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => `${r.EmpCode || r.empCode || ""} ${r.FirstName || r.firstName || ""} ${r.LastName || r.lastName || ""} ${r.Department || r.department || ""} ${r.Designation || r.designation || ""}`.toLowerCase().includes(q));
  }, [rows, search]);

  const totalSalary = rows.reduce((sum, r) => sum + Number(r.BasicSalary || r.basicSalary || 0), 0);
  const departmentCount = new Set(rows.map((r) => r.Department || r.department).filter(Boolean)).size;

  return (
    <div className="container-fluid py-2 app-small">
      <div className="hr-hero card border-0 shadow-sm mb-3">
        <div className="card-body d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div>
            <div className="small text-uppercase fw-bold opacity-75">Human Resources</div>
            <h4 className="mb-1 fw-bold">Employees</h4>
            <div className="opacity-75">Maintain employee master data, salary components and department details.</div>
          </div>
          <button className="btn btn-light btn-sm fw-semibold" onClick={load} type="button">
            <i className="bi bi-arrow-clockwise me-1"></i>Refresh
          </button>
        </div>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-md-4"><div className="hr-stat-card p-3"><div className="text-muted small">Total Employees</div><div className="h4 mb-0 fw-bold">{rows.length}</div></div></div>
        <div className="col-md-4"><div className="hr-stat-card p-3"><div className="text-muted small">Departments</div><div className="h4 mb-0 fw-bold">{departmentCount}</div></div></div>
        <div className="col-md-4"><div className="hr-stat-card p-3"><div className="text-muted small">Basic Salary Total</div><div className="h4 mb-0 fw-bold">₹{totalSalary.toFixed(2)}</div></div></div>
      </div>

      <div className="card border-0 shadow-sm mb-3 hr-form-card">
        <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
          <div><div className="hr-section-title">Employee Entry</div><div className="small text-muted">Required fields are employee code, first name, department, designation and join date.</div></div>
          <span className="badge rounded-pill text-bg-primary">Master</span>
        </div>
        <form className="card-body row g-3 pt-0" onSubmit={save}>
          <div className="col-xl-2 col-md-4"><label className="form-label">Emp Code</label><input className="form-control form-control-sm" value={form.empCode} onChange={e=>setForm({...form, empCode:e.target.value})}/></div>
          <div className="col-xl-2 col-md-4"><label className="form-label">First Name</label><input className="form-control form-control-sm" value={form.firstName} onChange={e=>setForm({...form, firstName:e.target.value})}/></div>
          <div className="col-xl-2 col-md-4"><label className="form-label">Last Name</label><input className="form-control form-control-sm" value={form.lastName} onChange={e=>setForm({...form, lastName:e.target.value})}/></div>
          <div className="col-xl-2 col-md-4"><label className="form-label">Department</label><input className="form-control form-control-sm" value={form.department} onChange={e=>setForm({...form, department:e.target.value})}/></div>
          <div className="col-xl-2 col-md-4"><label className="form-label">Designation</label><input className="form-control form-control-sm" value={form.designation} onChange={e=>setForm({...form, designation:e.target.value})}/></div>
          <div className="col-xl-2 col-md-4"><label className="form-label">Join Date</label><input type="date" className="form-control form-control-sm" value={form.joinDate} onChange={e=>setForm({...form, joinDate:e.target.value})}/></div>
          <div className="col-xl-2 col-md-4"><label className="form-label">Basic</label><input type="number" className="form-control form-control-sm text-end" value={form.basicSalary} onChange={e=>setForm({...form, basicSalary:e.target.value})}/></div>
          <div className="col-xl-2 col-md-4"><label className="form-label">HRA</label><input type="number" className="form-control form-control-sm text-end" value={form.hra} onChange={e=>setForm({...form, hra:e.target.value})}/></div>
          <div className="col-xl-2 col-md-4"><label className="form-label">Allowance</label><input type="number" className="form-control form-control-sm text-end" value={form.allowance} onChange={e=>setForm({...form, allowance:e.target.value})}/></div>
          <div className="col-xl-3 col-md-6 d-flex align-items-end gap-2">
            <button className="btn btn-primary btn-sm px-4" disabled={saving}>{saving ? "Saving..." : "Save Employee"}</button>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setForm(emptyForm)}>Clear</button>
          </div>
        </form>
      </div>

      <div className="card border-0 shadow-sm hr-table-card">
        <div className="card-header bg-white border-0 py-3 d-flex flex-wrap justify-content-between align-items-center gap-2">
          <div><div className="hr-section-title">Employee List</div><div className="small text-muted">Search and review employee master records.</div></div>
          <div className="input-group input-group-sm" style={{ maxWidth: 300 }}><span className="input-group-text bg-white"><i className="bi bi-search"></i></span><input className="form-control" placeholder="Search employee..." value={search} onChange={(e) => setSearch(e.target.value)} /></div>
        </div>
        <div className="table-responsive compact-table-wrap">
          <table className="table table-sm table-hover align-middle mb-0 compact-table">
            <thead><tr><th>Code</th><th>Name</th><th>Department</th><th>Designation</th><th>Join Date</th><th className="text-end">Basic</th><th className="text-end">HRA</th><th className="text-end">Allowance</th></tr></thead>
            <tbody>{filteredRows.length ? filteredRows.map((r,i)=><tr key={r.EmployeeId||r.employeeId||i}><td className="fw-semibold">{r.EmpCode||r.empCode}</td><td>{r.FirstName||r.firstName} {r.LastName||r.lastName}</td><td>{r.Department||r.department}</td><td>{r.Designation||r.designation}</td><td>{r.JoinDate||r.joinDate||"-"}</td><td className="text-end">{Number(r.BasicSalary||r.basicSalary||0).toFixed(2)}</td><td className="text-end">{Number(r.Hra||r.HRA||r.hra||0).toFixed(2)}</td><td className="text-end">{Number(r.Allowance||r.allowance||0).toFixed(2)}</td></tr>) : <tr><td colSpan="8" className="text-center text-muted py-4">No employees found</td></tr>}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
